import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { PostgrestFilterBuilder, PostgrestBuilder } from '@supabase/postgrest-js';
import type { Database } from '../lib/database.types';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

type TableName = keyof Database['public']['Tables'];

export function useSupabase() {
  // Return the supabase object with all namespaces (public, auth, secured)
  // For backward compatibility, maintain the common pattern of direct table access
  return { 
    // Return the full supabase object with all namespaces
    supabase,
    
    // For backward compatibility: route direct from() calls to the client
    from: (table: string) => supabase.client.from(table as any),
    
    // Direct client access for backward compatibility
    client: supabase.client,
    
    // Access to auth namespace
    auth: supabase.auth,
    
    // Access to public namespace
    public: supabase.public,
    
    // Access to secured namespace
    secured: supabase.secured
  };
}

export interface QueryOptions {
  select?: string;
  order?: { column: string; ascending?: boolean };
  limit?: number;
  filters?: { column: string; operator: string; value: any }[];
}

export function useSupabaseQuery<T extends TableName>(
  table: T,
  options: QueryOptions = {}
) {
  const [data, setData] = useState<any[]>([]);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Access the table through the client for authenticated operations
        // In the new pattern, we should use the client directly
        const query = supabase.client.from(table).select(options.select || '*');

        // Apply filters if any
        if (options.filters && options.filters.length > 0) {
          options.filters.forEach(filter => {
            // We need to use the filter method directly without reassigning query
            if (filter.operator === 'eq') {
              query.eq(filter.column, filter.value);
            } else if (filter.operator === 'neq') {
              query.neq(filter.column, filter.value);
            } else if (filter.operator === 'gt') {
              query.gt(filter.column, filter.value);
            } else if (filter.operator === 'gte') {
              query.gte(filter.column, filter.value);
            } else if (filter.operator === 'lt') {
              query.lt(filter.column, filter.value);
            } else if (filter.operator === 'lte') {
              query.lte(filter.column, filter.value);
            } else if (filter.operator === 'like') {
              query.like(filter.column, filter.value);
            } else if (filter.operator === 'ilike') {
              query.ilike(filter.column, filter.value);
            } else if (filter.operator === 'is') {
              query.is(filter.column, filter.value);
            } else if (filter.operator === 'in') {
              query.in(filter.column, filter.value);
            } else {
              // Default to eq if unsupported operator
              query.eq(filter.column, filter.value);
            }
          });
        }

        // Apply ordering if specified
        if (options.order) {
          query.order(options.order.column, { ascending: options.order.ascending ?? true });
        }

        // Apply limit if specified
        if (options.limit) {
          query.limit(options.limit);
        }

        // Execute the query
        const { data, error } = await query;

        if (error) throw error;
        setData(data || []);
      } catch (err) {
        console.error(`Error fetching from ${table}:`, err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [table, options.select, options.limit, JSON.stringify(options.filters), JSON.stringify(options.order)]);

  return { data, error, loading };
}
